/**
 * Copyright (C) 2020 Glenn Rioux
 *
 * This file is part of Event Manager.
 *
 * Event Manager is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 2 of the License, or
 * (at your option) any later version.
 *
 * Event Manager is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with Event Manager.  If not, see <http://www.gnu.org/licenses/>.
 */

import {
	AccountType,
	api,
	AsyncEither,
	asyncRight,
	CAPProspectiveMemberObject,
	ClientUser,
	Either,
	hasOneDutyPosition,
	hasPermission,
	isRegularCAPAccountObject,
	// isCAPMember,
	Permissions,
	reports,
	spreadsheets,
} from 'common-lib';
import { TDocumentDefinitions, TFontDictionary } from 'pdfmake/interfaces';
import * as React from 'react';
import Button from '../../../components/Button';
import { FetchAPIProps, withFetchApi } from '../../../globals';
import { clientErrorGenerator } from '../../../lib/error';
import Page, { PageProps } from '../../Page';
import type { WritingOptions } from 'xlsx-js-style';

interface ReportsWidgetLoadingState {
	state: 'LOADING';
}

interface ReportsWidgetLoadedState {
	state: 'LOADED';

	nhqMembers: api.member.promotionrequirements.PromotionRequirementsItem[];
	newMembers: CAPProspectiveMemberObject[];
	cpptStatuses: api.member.cpptstatus.CPPTStatusItem[];
}

interface ReportsWidgetErrorState {
	state: 'ERROR';

	error: string;
}

type ReportsWidgetState =
	| ReportsWidgetErrorState
	| ReportsWidgetLoadedState
	| ReportsWidgetLoadingState;

interface ReportsWidgetViewState {
	showError: boolean;
}

const hasAllowedDutyPosition = hasOneDutyPosition([
	'Cadet Flight Commander',
	'Cadet Flight Sergeant',
	'Cadet Commander',
	'Cadet Deputy Commander for Operations',
	'Cadet Deputy Commander for Support ',
	'Deputy Commander For Cadets',
]);

const getUpperLevelOrgId = (props: PageProps): number | null => {
	if (!isRegularCAPAccountObject(props.account)) {
		return null;
	}

	if (props.account.type !== AccountType.CAPGROUP && props.account.type !== AccountType.CAPWING) {
		return null;
	}

	return props.account.orgid;
};

const canAccessUpperLevelReports = (props: PageProps): boolean => {
	if (!props.member || !isRegularCAPAccountObject(props.account)) {
		return false;
	}

	if (props.account.type !== AccountType.CAPGROUP && props.account.type !== AccountType.CAPWING) {
		return false;
	}

	const upperLevelOrgId = getUpperLevelOrgId(props);

	if (upperLevelOrgId === null) {
		return false;
	}

	const memberAssignedInScope = props.member.orgid === upperLevelOrgId;
	const hasDutyInScope = props.member.dutyPositions.some(
		duty => duty.type === 'NHQ' && duty.orgid === upperLevelOrgId,
	);

	return memberAssignedInScope || hasDutyInScope;
};

export const shouldRenderReports = (props: PageProps): boolean => {
	if (!props.member || !isRegularCAPAccountObject(props.account)) {
		return false;
	}

	if (props.account.type === AccountType.CAPGROUP || props.account.type === AccountType.CAPWING) {
		return canAccessUpperLevelReports(props);
	}

	return (
		hasAllowedDutyPosition(props.member) ||
		hasPermission('PromotionManagement')(Permissions.PromotionManagement.FULL)(props.member)
	);
};

export const canUseReports = (props: PageProps): boolean => {
	if (!props.member) {
		return false;
	}

	return shouldRenderReports(props);
};

export interface RequiredMember extends PageProps, FetchAPIProps {
	member: ClientUser;
}

export const ReportsWidget = withFetchApi(
	class extends Page<RequiredMember, ReportsWidgetState & ReportsWidgetViewState> {
		public state: ReportsWidgetState & ReportsWidgetViewState = {
			state: 'LOADING',
			showError: false,
		};

		public async componentDidMount(): Promise<void> {
			if (!this.props.member) {
				return;
			}

			const sharedRequests = await AsyncEither.All([
				this.props.fetchApi.member.cpptstatus({}, {}),
				// Ignore; this just preemptively loads pdfmake so we can use it whenever without actually going out to get the JS
				asyncRight(import('pdfmake'), clientErrorGenerator('Could not import pdfmake')),
				asyncRight(import('xlsx-js-style'), clientErrorGenerator('Could not import xlsx-js-style')),
				asyncRight(import('xlsx'), clientErrorGenerator('Could not import xlsx-js-style')),
			]);

			if (Either.isLeft(sharedRequests)) {
				this.setState(prev => ({
					...prev,
					state: 'ERROR',
					error: sharedRequests.value.message,
				}));
				return;
			}

			const [cpptStatuses] = sharedRequests.value;

			if (this.props.account.type !== AccountType.CAPSQUADRON) {
				this.setState(prev => ({
					...prev,
					state: 'LOADED',
					newMembers: [],
					nhqMembers: [],
					cpptStatuses,
				}));
				return;
			}

			const sqrRequests = await AsyncEither.All([
				this.props.fetchApi.member.promotionrequirements.account({}, {}),
				this.props.fetchApi.member.memberList({ type: 'CAPProspectiveMember' }, {}),
			]);

			if (Either.isLeft(sqrRequests)) {
				this.setState(prev => ({
					...prev,
					state: 'ERROR',
					error: sqrRequests.value.message,
				}));
				return;
			}

			const [nhqMembers, newMembers] = sqrRequests.value;

			this.setState(prev => ({
				...prev,
				state: 'LOADED',
				newMembers,
				nhqMembers,
				cpptStatuses,
			}));
	}

	public render = (): React.ReactElement => {
		const isLoaded = this.state.state === 'LOADED';
		const canViewSqrReports = this.props.account.type === AccountType.CAPSQUADRON;

		return (
			<div className="widget">
				<div className="widget-title">Reports</div>
				<div className="widget-body">
					{this.state.state === 'ERROR' ? (
						<div>{this.state.error}</div>
					) : (
						<>
							{canViewSqrReports && (
								<>
									<div>
										{isLoaded ? (
											<>
												SQR 52-1 Cadet Orientation Flight report &nbsp;
												<Button
													buttonType="none"
													onClick={this.createsqr521Spreadsheet}
												>
													xlsx
												</Button>
											</>
										) : (
											this.renderCompactLoader()
										)}
										<br />
									</div>
									<div>
										{isLoaded ? (
											<>
												SQR 60-1 Cadet status report &nbsp;
												<Button onClick={() => this.createSQR601()} buttonType="none">
													pdf
												</Button>{' '}
												&nbsp;
												<Button
													buttonType="none"
													onClick={this.createsqr601Spreadsheet}
												>
													xlsx
												</Button>
											</>
										) : (
											this.renderCompactLoader()
										)}
										<br />
									</div>
									<div>
										{isLoaded ? (
											<>
												SQR 60-1a Cadet status report by flight &nbsp;
												<Button
													buttonType="none"
													onClick={this.createsqr601aSpreadsheet}
												>
													xlsx
												</Button>
											</>
										) : (
											this.renderCompactLoader()
										)}
										<br />
									</div>
									<div>
										{isLoaded ? (
											<>
												SQR 60-20 Cadet HFZ report &nbsp;
												<Button onClick={() => this.createSQR6020()} buttonType="none">
													pdf
												</Button>{' '}
												&nbsp;
												<Button
													buttonType="none"
													onClick={this.createsqr6020Spreadsheet}
												>
													xlsx
												</Button>
											</>
										) : (
											this.renderCompactLoader()
										)}
										<br />
									</div>
								</>
							)}
							<div>
								{isLoaded ? (
									<>
										RPT 60-3 CPPT Status &nbsp;
										<Button
											buttonType="none"
											onClick={this.createrpt603Spreadsheet}
										>
											xlsx
										</Button>
									</>
								) : (
									this.renderCompactLoader()
								)}
								<br />
							</div>
						</>
					)}
				</div>
			</div>
		);
	};

	private renderCompactLoader = (): React.ReactElement => (
		<span
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				height: '1.25em',
			}}
		>
			<span
				style={{
					display: 'inline-block',
					width: '14rem',
					height: '0.7em',
					borderRadius: '999px',
					backgroundColor: '#d7dee5',
				}}
			/>
		</span>
	);

		private createSQR601 = async (): Promise<void> => {
			if (this.state.state === 'ERROR') {
				this.setState({
					showError: true,
				});

				return;
			}

			if (this.state.state !== 'LOADED' || !this.props.member) {
				return;
			}

			const now = new Date();
			const formatdate =
				now.getFullYear().toString() +
				'-' +
				(now.getMonth() + 1).toString().padStart(2, '0') +
				'-' +
				now.getDate().toString().padStart(2, '0') +
				' ' +
				now.getHours().toString().padStart(2, '0') +
				now.getMinutes().toString().padStart(2, '0');

			const docDef = reports.sqr601DocumentDefinition(
				this.state.nhqMembers,
				this.state.newMembers,
				this.props.registry,
			);

			await this.printForm(docDef, `SQR601-${this.props.account.id}-${formatdate}.pdf`);
		};

		private createSQR6020 = async (): Promise<void> => {
			if (this.state.state === 'ERROR') {
				this.setState({
					showError: true,
				});

				return;
			}

			if (this.state.state !== 'LOADED' || !this.props.member) {
				return;
			}

			const now = new Date();
			const formatdate =
				now.getFullYear().toString() +
				'-' +
				(now.getMonth() + 1).toString().padStart(2, '0') +
				'-' +
				now.getDate().toString().padStart(2, '0') +
				' ' +
				now.getHours().toString().padStart(2, '0') +
				now.getMinutes().toString().padStart(2, '0');

			const docDef = reports.sqr6020DocumentDefinition(
				this.state.nhqMembers,
				this.state.newMembers,
				this.props.registry,
			);

			await this.printForm(docDef, `SQR6020-${this.props.account.id}-${formatdate}.pdf`);
		};

		private async printForm(docDef: TDocumentDefinitions, fileName: string): Promise<void> {
			const pdfMake = await import('pdfmake');

			const fontGetter =
				process.env.NODE_ENV === 'production'
					? (fontName: string) =>
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							`https://${this.props.account.id}.${process.env
								.REACT_APP_HOST_NAME!}/images/fonts/${fontName}`
					: (fontName: string) => `http://localhost:3000/images/fonts/${fontName}`;

			const fonts: TFontDictionary = {
				FreeSans: {
					normal: fontGetter('FreeSans.ttf'),
					bold: fontGetter('FreeSansBold.ttf'),
					italics: fontGetter('FreeSansOblique.ttf'),
					bolditalics: fontGetter('FreeSansBoldOblique.ttf'),
				},
			};

			// @ts-ignore: the types lie, as has been verified with tests
			const docPrinter = (pdfMake.createPdf as (
				def: TDocumentDefinitions,
				idk: null,
				fontDictionary: TFontDictionary,
			) => { download(filename: string): void })(docDef, null, fonts);

			docPrinter.download(fileName);
		}

		private createsqr6020Spreadsheet = async (): Promise<void> => {
			if (this.state.state !== 'LOADED' || !this.props.member) {
				return;
			}

			const XLSX = await import('xlsx');

			const wb = XLSX.utils.book_new();

			let wsName = 'UnitInfo';
			const wsDataEvent = spreadsheets.sqr6020XL();
			let ws = XLSX.utils.aoa_to_sheet(wsDataEvent);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			let sheet = spreadsheets.Formatsqr6020XL(ws);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);

			wsName = 'HFZStatus';
			const [wsDataAttendance, widths] = spreadsheets.sqr6020MembersXL(
				this.state.nhqMembers,
				this.state.newMembers,
				// this.props.registry,
			);
			ws = XLSX.utils.aoa_to_sheet(wsDataAttendance);
			sheet = spreadsheets.Formatsqr6020MembersXL(ws, widths, wsDataAttendance.length);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);

			const now = new Date();
			const formatdate =
				now.getFullYear().toString() +
				'-' +
				(now.getMonth() + 1).toString().padStart(2, '0') +
				'-' +
				now.getDate().toString().padStart(2, '0') +
				' ' +
				now.getHours().toString().padStart(2, '0') +
				now.getMinutes().toString().padStart(2, '0');

			XLSX.writeFile(wb, `SQR 60-20 ${this.props.account.id}-${formatdate}.xlsx`);
		};

		private createrpt603Spreadsheet = async (): Promise<void> => {
			if (this.state.state !== 'LOADED' || !this.props.member) {
				return;
			}

			const XLSX = await import('xlsx-js-style');
			const opts: WritingOptions = {
				cellDates: true,
				cellStyles: true,
			};

			const wb = XLSX.utils.book_new();

			let wsName = 'UnitInfo';
			const wsDataEvent = spreadsheets.rpt603CPPTXL();
			let ws = XLSX.utils.aoa_to_sheet(wsDataEvent);
			let sheet = spreadsheets.Formatrpt603CPPTXL(ws);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);

			wsName = 'CPPTStatus';
			const [wsDataAttendance, widths] = spreadsheets.rpt603CPPTMembersXL(
				this.state.cpptStatuses,
			);
			ws = XLSX.utils.aoa_to_sheet(wsDataAttendance);
			sheet = spreadsheets.Formatrpt603CPPTMembersXL(
				ws,
				widths,
				wsDataAttendance.length,
			);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);

			const now = new Date();
			const formatdate =
				now.getFullYear().toString() +
				'-' +
				(now.getMonth() + 1).toString().padStart(2, '0') +
				'-' +
				now.getDate().toString().padStart(2, '0') +
				' ' +
				now.getHours().toString().padStart(2, '0') +
				now.getMinutes().toString().padStart(2, '0');

			XLSX.writeFile(wb, `RPT 60-3 ${this.props.account.id}-${formatdate}.xlsx`, opts);
		};

		private createsqr601Spreadsheet = async (): Promise<void> => {
			if (this.state.state !== 'LOADED' || !this.props.member) {
				return;
			}

			const XLSX = await import('xlsx');

			const wb = XLSX.utils.book_new();

			let wsName = 'UnitInfo';
			const wsDataEvent = spreadsheets.sqr601XL();
			let ws = XLSX.utils.aoa_to_sheet(wsDataEvent);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			let sheet = spreadsheets.Formatsqr601XL(ws);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);

			wsName = 'CadetInfo';
			const [wsDataAttendance, widths] = spreadsheets.sqr601MembersXL(
				this.state.nhqMembers,
				this.state.newMembers,
				// this.props.registry,
			);
			ws = XLSX.utils.aoa_to_sheet(wsDataAttendance);
			sheet = spreadsheets.Formatsqr601MembersXL(ws, widths, wsDataAttendance.length);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);

			const now = new Date();
			const formatdate =
				now.getFullYear().toString() +
				'-' +
				(now.getMonth() + 1).toString().padStart(2, '0') +
				'-' +
				now.getDate().toString().padStart(2, '0') +
				' ' +
				now.getHours().toString().padStart(2, '0') +
				now.getMinutes().toString().padStart(2, '0');

			XLSX.writeFile(wb, `SQR 60-1 ${this.props.account.id}-${formatdate}.xlsx`);
		};

		private createsqr601aSpreadsheet = async (): Promise<void> => {
			if (this.state.state !== 'LOADED' || !this.props.member) {
				return;
			}

			const XLSX = await import('xlsx');

			const wb = XLSX.utils.book_new();

			let wsName = 'UnitInfo';
			const wsDataEvent = spreadsheets.sqr601aXL();
			let ws = XLSX.utils.aoa_to_sheet(wsDataEvent);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			let sheet = spreadsheets.Formatsqr601aXL(ws);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);
			const flights = this.props.registry.RankAndFile.Flights; 
			const state = this.state;

			flights.forEach(flight => {
				// need to loop through flights and create a sheet for each flight
				wsName = flight.toString();
				console.log("flight", wsName);
				if(
					state.nhqMembers.filter((member: api.member.promotionrequirements.PromotionRequirementsItem) => member.member.flight === flight).length +
					state.newMembers.filter((member: CAPProspectiveMemberObject) => member.flight === flight).length > 0
				) {
					const [wsDataAttendance, widths]: [any[][], number[]] = spreadsheets.sqr601aMembersXL(
						state.nhqMembers.filter((member: api.member.promotionrequirements.PromotionRequirementsItem) => member.member.flight === flight),
						state.newMembers.filter((member: CAPProspectiveMemberObject) => member.flight === flight),
					);

					ws = XLSX.utils.aoa_to_sheet(wsDataAttendance);
					sheet = spreadsheets.Formatsqr601aMembersXL(ws, widths, wsDataAttendance.length); 
					XLSX.utils.book_append_sheet(wb, sheet, wsName);
				}
			});

			const now = new Date();
			const formatdate =
				now.getFullYear().toString() +
				'-' +
				(now.getMonth() + 1).toString().padStart(2, '0') +
				'-' +
				now.getDate().toString().padStart(2, '0') +
				' ' +
				now.getHours().toString().padStart(2, '0') +
				now.getMinutes().toString().padStart(2, '0');

			XLSX.writeFile(wb, `SQR 60-1a ${this.props.account.id}-${formatdate}.xlsx`);
		};

		private createsqr521Spreadsheet = async (): Promise<void> => {
			if (this.state.state !== 'LOADED' || !this.props.member) {
				return;
			}

			const XLSX = await import('xlsx-js-style');
			const opts: WritingOptions = {
				cellDates: true,
				cellStyles: true,
			};

			const wb = XLSX.utils.book_new();
			// wb.Props = {};

			let wsName = 'UnitInfo';
			const wsDataEvent = spreadsheets.sqr521XL();
			let ws = XLSX.utils.aoa_to_sheet(wsDataEvent);
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			let sheet = spreadsheets.Formatsqr521XL(ws);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);
			// const flights = this.props.registry.RankAndFile.Flights; 
			// const state = this.state;

			wsName = 'CadetInfo';
			const [wsDataAttendance, widths] = spreadsheets.sqr521MembersXL(
				this.state.nhqMembers,
				// this.state.newMembers,
				// this.props.registry,
			);
			ws = XLSX.utils.aoa_to_sheet(wsDataAttendance);
			sheet = spreadsheets.Formatsqr521MembersXL(ws, widths, wsDataAttendance.length);
			XLSX.utils.book_append_sheet(wb, sheet, wsName);

			const now = new Date();
			const formatdate =
				now.getFullYear().toString() +
				'-' +
				(now.getMonth() + 1).toString().padStart(2, '0') +
				'-' +
				now.getDate().toString().padStart(2, '0') +
				' ' +
				now.getHours().toString().padStart(2, '0') +
				now.getMinutes().toString().padStart(2, '0');

			XLSX.writeFile(wb, `SQR 52-1 ${this.props.account.id}-${formatdate}.xlsx`, opts);
		};


	},
);
